/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Form, Input, Switch } from "core/components";
import { navigateTo } from "core/navigation";
import { TaskClassificationFormData, TaskClassificationSchema } from "core/schema";
import { admin_task_classifications_path, admin_task_classification_path } from "routes";

type TaskClassificationFormProps = {
  task_classification: TaskClassificationFormData;
};

const TaskClassificationForm = ({ task_classification }: TaskClassificationFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<TaskClassificationFormData>({
    mode: "onChange",
    defaultValues: TaskClassificationSchema.cast(task_classification),
    resolver: yupResolver(TaskClassificationSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const save = async (data: TaskClassificationFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_task_classification_path(task_classification), data);
      } else {
        await api.post(admin_task_classifications_path(task_classification), data);
      }

      navigateTo(admin_task_classifications_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={task_classification.id ? "Edit Task Classification" : "New Task Classification"}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_task_classifications_path())}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={3} />
                <Col>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  <Input
                    {...register("code")}
                    label="Code"
                    required
                    errorMessage={errors.code?.message}
                    className="field text-uppercase"
                    maxLength={30}
                  />
                  <Input {...register("name")} label="Name" required errorMessage={errors.name?.message} maxLength={100} />
                </Col>
                <Col lg={3} />
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default TaskClassificationForm;
